"use client";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { hexToRgba } from "@/utils/utils";
import Image from "next/image";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

const ChevronDownIcon = ({ color = "#FFFFFF" }: { color?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95"
      stroke={color}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Variant 2 (light theme) — "export" icon next to the Apply Now button (matches Figma vuesax/linear/export).
const ExportIcon = ({ color = "#FFFFFF" }: { color?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12"
      stroke={color}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 11L21.2 2.8"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6.83V2H17.17"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface WhoIsThisProgramForMeta {
  id?: number | string;
  meta_title?: string;
  meta_title_color?: string;
  meta_title_hover_color?: string;
  meta_description?: string;
  meta_description_color?: string;
  meta_image?: string;
  meta_priority?: number;
}

interface WhoIsThisProgramForData {
  desktop_background_color?: string;
  mobile_background_color?: string;
  backvariant?: "variant_1" | "variant_2" | "variant_3";
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;
  pre_title?: string;
  pre_title_color?: string;
  title?: string;
  title_start_color?: string;
  title_end_color?: string;
  title_gradient_angle?: string;
  description?: string;
  description_color?: string;
  cta_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  design_variant?: string;
  v2_background_color?: string;
  v2_heading_color?: string;
  v2_description_color?: string;
  v2_accent_color?: string;
  v2_card_title_color?: string;
  v2_button_text_color?: string;
  enable_animation?: boolean | number;
  status?: boolean;
  metadata?: WhoIsThisProgramForMeta[];
}

interface WhoIsThisProgramForProps {
  data: {
    data: WhoIsThisProgramForData;
  };
  className?: string;
}

function WhoIsThisProgramFor1({ data, className }: WhoIsThisProgramForProps) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const sectionData = (data?.data ?? data ?? {}) as WhoIsThisProgramForData;
  const [activeIndex, setActiveIndex] = useState(0);

  const {
    pre_title = "",
    pre_title_color = "rgba(255,255,255,0.8)",
    title = "",
    title_start_color = "#FFFFFF",
    title_end_color = "#02A7B6",
    title_gradient_angle = "90deg",
    description = "",
    description_color = "rgba(255,255,255,0.7)",
    cta_text = "",
    cta_text_color = "#222222",
    cta_color = "#FFFFFF",
    status = true,
    metadata = [],
    enable_animation,
    desktop_background_color = "#000000",
    mobile_background_color = "#000000",
    backvariant = "variant_1",
    gradient_color_1 = "#00D4EB",
    gradient_color_2 = "#000000",
    gradient_color_3 = "#000000",
    gradient_color_4 = "#5345C3",
    design_variant = "variant_1",
    v2_background_color = "#FFFFFF",
    v2_heading_color = "#101112",
    v2_description_color = "#101112",
    v2_accent_color = "#9F1C34",
    v2_card_title_color = "#000000",
    v2_button_text_color = "#FFFFFF",
  } = sectionData;

  const isAnimationEnabled =
    enable_animation === true || enable_animation === 1;

  const sortedMetadata = useMemo(() => {
    if (!metadata?.length) return [];
    return [...metadata].sort(
      (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
    );
  }, [metadata]);

  const activeImage = sortedMetadata[activeIndex]?.meta_image;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "who_is_this_program_for_section");
  //   }
  // }, [status]);

  const handleCtaClick = useCallback(() => {
    leadCtaClick({
      section: "who_is_this_program_for_section",
      component: "cta_button",
      label: cta_text,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      })
    );
  }, [dispatch, cta_text]);

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
      case "variant_3":
        return "none";
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%)`;
    }
  };

  const hasContent =
    !!status ||
    pre_title ||
    title ||
    sortedMetadata.length > 0;
  if (!hasContent) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // Variant 2 — Figma light theme. Rendered as a separate branch so Variant 1
  // (the original dark/gradient design) stays completely untouched.
  // ─────────────────────────────────────────────────────────────────────────
  if (design_variant === "variant_2") {
    const accent = v2_accent_color || "#9F1C34";
    const activeCardBg = hexToRgba(accent, 0.08);
    const headingColor = v2_heading_color || "#101112";
    const descColor = v2_description_color || "#101112";
    const cardTitleColor = v2_card_title_color || "#000000";
    const buttonTextColor = v2_button_text_color || "#FFFFFF";
    const headingText = [pre_title, title]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(" ");

    return (
      <section
        ref={ref}
        className={cn(
          "relative w-full flex justify-center",
          "px-[24px] py-[40px]",
          // Figma component reference: 80px horizontal padding from sm up; mobile keeps its 24px.
          "sm:px-[80px] sm:py-[40px]",
          "md:py-[60px]",
          "lg:py-[60px]",
          "xl:py-[80px]",
          className
        )}
        style={{ backgroundColor: v2_background_color || "#FFFFFF" }}
      >
        <div
          className={cn(
            "relative w-full flex flex-col items-center",
            "gap-[40px] sm:gap-[44px]",
            "sm:w-[960px]",
            "md:w-[1110px]",
            "lg:w-[1270px]",
            "xl:w-[1471px]"
          )}
        >
          {/* Header */}
          <div
            className={cn(
              "flex flex-col w-full",
              "gap-[14px] sm:gap-[16px] md:gap-[20px] lg:gap-[24px]"
            )}
          >
            {headingText && (
              <p
                className={cn(
                  "w-full text-center font-semibold",
                  "text-[28px] sm:text-[30px] md:text-[32px] lg:text-[36px]",
                  "leading-[1.25] tracking-normal lg:tracking-[-0.54px]"
                )}
                style={{
                  color: headingColor,
                  fontFamily: "var(--font-poppins)",
                }}
              >
                {headingText}
              </p>
            )}
            {description && (
              <p
                className={cn(
                  "w-full text-center font-medium leading-[1.65]",
                  "text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]"
                )}
                style={{
                  color: descColor,
                  fontFamily: "var(--font-poppins)",
                }}
              >
                {description}
              </p>
            )}
          </div>

          {/* Content: accordion list + image */}
          <div
            className={cn(
              "flex w-full flex-col",
              "sm:flex-row sm:items-start",
              "sm:gap-[24px] md:gap-[40px] lg:gap-[60px]"
            )}
          >
            {/* List */}
            <div
              className={cn(
                "flex flex-col w-full gap-[10px]",
                "sm:flex-1 sm:min-w-0 sm:gap-0 sm:self-stretch sm:justify-between"
              )}
            >
              {sortedMetadata.map((item, index) => {
                const isActive = activeIndex === index;
                if (isActive) {
                  return (
                    <RevealOnScroll
                      key={item.id ?? index}
                      y={-100}
                      enableAnimation={isAnimationEnabled}
                    >
                      <button
                        type="button"
                        data-cta-name="tab_switch"
                        onClick={() => setActiveIndex(index)}
                        className={cn(
                          "text-left w-full cursor-pointer flex flex-col",
                          "gap-[20px] sm:gap-0",
                          "p-[10px] sm:p-[14px] md:p-[16px] lg:p-[20px]"
                        )}
                        style={{
                          backgroundColor: activeCardBg,
                          borderTop: `1px solid ${accent}`,
                          borderBottom: `1px solid ${accent}`,
                        }}
                      >
                        <div className="flex flex-col w-full gap-[4px] sm:gap-[6px]">
                          <p
                            className={cn(
                              "w-full font-semibold",
                              "text-[16px] md:text-[17px]",
                              "leading-[1.4] sm:leading-[1.65] tracking-[-0.08px] sm:tracking-normal"
                            )}
                            style={{
                              color: item.meta_title_hover_color || accent,
                              fontFamily: "var(--font-poppins)",
                            }}
                          >
                            {item.meta_title}
                          </p>
                          {item.meta_description && (
                            <p
                              className={cn(
                                "w-full font-medium",
                                "text-[14px] sm:text-[13px]",
                                "leading-[1.65] sm:leading-[1.5] sm:tracking-[0.13px]"
                              )}
                              style={{
                                color:
                                  item.meta_description_color || descColor,
                                fontFamily: "var(--font-poppins)",
                              }}
                            >
                              {item.meta_description}
                            </p>
                          )}
                        </div>

                        {/* Mobile: image inside the active card */}
                        {item.meta_image && (
                          <div className="relative w-full aspect-590/452 sm:hidden">
                            <Image
                              src={item.meta_image}
                              alt={item.meta_title ?? "Program target audience"}
                              fill
                              className="object-cover"
                              sizes="(max-width: 1079px) 100vw, 460px"
                            />
                          </div>
                        )}
                      </button>
                    </RevealOnScroll>
                  );
                }

                // The card directly above the active one drops its bottom
                // border so it doesn't stack with the active card's accent
                // top-border into a double line.
                const isAboveActive = activeIndex === index + 1;
                return (
                  <RevealOnScroll
                    key={item.id ?? index}
                    y={-100}
                    enableAnimation={isAnimationEnabled}
                  >
                    <button
                      type="button"
                      data-cta-name="tab_switch"
                      onClick={() => setActiveIndex(index)}
                      className={cn(
                        "text-left w-full cursor-pointer flex items-center justify-between gap-[6px]",
                        isAboveActive
                          ? "border-b-0"
                          : "border-solid border-b-[1px] sm:border-b-[0.6px]",
                        "p-[10px] sm:p-[14px] md:p-[16px] lg:p-[20px]"
                      )}
                      style={
                        isAboveActive
                          ? undefined
                          : { borderBottomColor: cardTitleColor }
                      }
                    >
                      <p
                        className={cn(
                          "flex-1 min-w-0 font-semibold",
                          "text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]",
                          "leading-[1.4] sm:leading-[1.65] tracking-[-0.07px] sm:tracking-normal"
                        )}
                        style={{
                          color: cardTitleColor,
                          fontFamily: "var(--font-poppins)",
                        }}
                      >
                        {item.meta_title}
                      </p>
                      {/* Mobile-only accordion chevron */}
                      <span className="shrink-0 sm:hidden">
                        <ChevronDownIcon color={cardTitleColor} />
                      </span>
                    </button>
                  </RevealOnScroll>
                );
              })}
            </div>

            {/* Desktop: image on the right */}
            {activeImage && (
              <RevealOnScroll
                scaleStart={0.5}
                x={100}
                enableAnimation={isAnimationEnabled}
              >
                <div
                  className={cn(
                    "relative overflow-hidden shrink-0",
                    "hidden sm:block",
                    "sm:w-[460px] sm:h-[353px]",
                    "md:w-[500px] md:h-[384px]",
                    "lg:w-[590px] lg:h-[452px]"
                  )}
                >
                  <Image
                    src={activeImage}
                    alt={
                      sortedMetadata[activeIndex]?.meta_title ??
                      "Program target audience"
                    }
                    fill
                    className="object-cover transition-opacity duration-300"
                    sizes="(max-width: 1229px) 460px, (max-width: 1429px) 500px, 590px"
                  />
                </div>
              </RevealOnScroll>
            )}
          </div>

          {/* CTA Button (kept on mobile even though the mobile Figma omits it) */}
          {cta_text && (
            <div className="flex justify-center w-full">
              <RevealOnScroll enableAnimation={isAnimationEnabled}>
                <button
                  type="button"
                  data-cta-name="apply_now"
                  onClick={handleCtaClick}
                  className={cn(
                    "w-full sm:w-auto flex items-center justify-center gap-[10px] cursor-pointer",
                    "rounded-[8px] px-[24px] py-[16px]",
                    "drop-shadow-[0px_10px_10px_rgba(202,125,139,0.6)]"
                  )}
                  style={{ backgroundColor: accent }}
                >
                  <span
                    className="font-semibold text-[16px] leading-[1.4] whitespace-nowrap"
                    style={{
                      color: buttonTextColor,
                      fontFamily: "var(--font-poppins)",
                    }}
                  >
                    {cta_text}
                  </span>
                  <ExportIcon color={buttonTextColor} />
                </button>
              </RevealOnScroll>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className={cn(
        "who-is-program-section relative w-full overflow-hidden flex justify-center",
        "xs:px-[24px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[60px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]",
        className
      )}
      style={{ backgroundColor: desktop_background_color }}
    >
      <style>{`
        @media (max-width: 1079px) {
          .who-is-program-section {
            background-color: ${mobile_background_color} !important;
          }
        }
      `}</style>

      <div
        className="absolute inset-0 z-0"
        style={{ background: getGradientBackground() }}
      />

      <div
        className={cn(
          "relative z-10 w-full flex flex-col",
          "xs:gap-[24px]",
          "sm:w-[960px] sm:gap-0",
          "md:w-[1110px]",
          "lg:w-[1270px]",
          "xl:w-[1471px]"
        )}
      >
        {/* Header: pre-title + gradient title */}
        <div
          className={cn(
            "flex flex-col",
            "xs:gap-[4px]",
            "sm:gap-[2px] sm:mb-[24px]",
            "md:gap-0 md:mb-[30px]",
            "lg:gap-0 lg:mb-[24px]",
            "xl:gap-0 xl:mb-[24px]"
          )}
        >
          {pre_title && (
            <p
              className={cn(
                "white-title-wf line-clamp-2",
                "text-[24px]!",
                "sm:text-[28px]!",
                "md:text-[30px]!",
                "lg:text-[30px]!",
                "xl:text-[30px]!",
              )}
              style={{
                color: pre_title_color,
                fontFamily: "var(--font-google-sans)",
              }}
            >
              {pre_title}
            </p>
          )}
          {title && (
            <p
              className="gradient-title-denton line-clamp-2"
              style={{
                backgroundImage: `linear-gradient(${title_gradient_angle}, ${title_start_color}, ${title_end_color})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "var(--font-merriweather)",
              }}
            >
              {title}
            </p>
          )}
        </div>

        {/* Description */}
        {description && (
          <p
            className={cn(
              "xs:text-[14px] xs:leading-[1.49]",
              "sm:text-[12px] sm:leading-[1.49] sm:mb-[42px] sm:w-[640px] sm:h-[36px]",
              "md:text-[14px] md:leading-[1.49] md:mb-[42px] md:w-[740px] md:h-[42px]",
              "lg:text-[16px] lg:leading-[1.49] lg:mb-[40px] lg:w-[843px] lg:h-[48px]",
              "xl:text-[16px] xl:leading-[1.49] xl:mb-[60px] xl:w-[843px] xl:h-[48px]"
            )}
            style={{
              color: description_color,
              fontFamily: "var(--font-google-sans)",
              fontWeight: 600,
            }}
          >
            {description}
          </p>
        )}

        {/* Pointers + Image layout */}
        <div
          className={cn(
            "flex",
            "xs:flex-col",
            "sm:flex-row sm:gap-[25px]",
            "md:flex-row md:gap-[60px]",
            "lg:gap-[60px]",
            "xl:gap-[100px]"
          )}
        >
          {/* Left: Pointer list */}
          <div
            className={cn(
              "flex flex-col",
              "xs:w-full xs:gap-[16px]",
              "sm:w-[475px] sm:shrink-0 sm:gap-[12px]",
              "md:w-[550px] md:shrink-0 md:gap-[14px]",
              "lg:w-[620px] lg:shrink-0 lg:gap-[15px]",
              "xl:w-[781px] xl:shrink-0 xl:gap-[15px]"
            )}
          >
            {sortedMetadata.map((item, index) => {
              const isActive = activeIndex === index;
              const isAboveActive = activeIndex === index + 1;
              const activeBorderColor = "#FFFFFF";
              const inactiveBorderColor = "rgba(255,255,255,0.3)";
              const inactiveTextColor = "rgba(255,255,255,0.5)";
              return (
                <RevealOnScroll
                  key={item.id ?? index}
                  y={-100}
                  enableAnimation={isAnimationEnabled}
                >
                <button
                  key={item.id ?? index}
                  type="button"
                  data-cta-name="tab_switch"
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    "text-left w-full cursor-pointer transition-colors duration-300",
                    "xs:p-[10px]",
                    "sm:px-[15px] sm:py-[15px]",
                    "md:px-[16px] md:py-[16px]",
                    "lg:px-[20px] lg:py-[20px]",
                    "xl:px-[40px] xl:py-[20px]",
                    isActive
                      ? "bg-[rgba(255,255,255,0.1)]"
                      : "bg-transparent"
                  )}
                  style={{
                    borderStyle: "solid",
                    borderLeftWidth: "0",
                    borderRightWidth: "0",
                    borderTopWidth: isActive ? "1px" : index === 0 ? "1px" : "0",
                    borderBottomWidth: isActive ? "1px" : isAboveActive ? "0" : "2px",
                    borderTopColor: isActive ? activeBorderColor : inactiveBorderColor,
                    borderBottomColor: isActive ? activeBorderColor : inactiveBorderColor,
                  }}
                >
                  {/* Mobile inactive: title + chevron in a row */}
                  {!isActive && (
                    <div className="flex items-center justify-between gap-[6px] sm:hidden">
                      <p
                        className="line-clamp-1 font-semibold leading-[1.49] text-[14px] flex-1 min-w-0"
                        style={{
                          color: inactiveTextColor,
                          fontFamily: "var(--font-google-sans)",
                        }}
                      >
                        {item.meta_title}
                      </p>
                      <ChevronDownIcon
                        color={inactiveTextColor}
                      />
                    </div>
                  )}

                  {/* Desktop: always show title (active uses hover color) */}
                  <p
                    className={cn(
                      "line-clamp-1 font-semibold leading-[1.49]",
                      isActive
                        ? "xs:text-[18px]"
                        : "xs:hidden",
                      "sm:block sm:text-[15px]",
                      "md:text-[16px]",
                      "lg:text-[20px]",
                      "xl:text-[20px]"
                    )}
                    style={{
                      color: isActive
                        ? (item.meta_title_hover_color ?? "#4CECFA")
                        : inactiveTextColor,
                      fontFamily: "var(--font-google-sans)",
                    }}
                  >
                    {item.meta_title}
                  </p>

                  {/* Active description */}
                  {isActive && item.meta_description && (
                    <p
                      className={cn(
                        "font-semibold leading-[1.49]",
                        "xs:text-[14px] xs:mt-[6px]",
                        "sm:text-[12px] sm:mt-[4px] sm:line-clamp-2",
                        "md:text-[12px] md:mt-[6px]",
                        "lg:text-[14px] lg:mt-[6px]",
                        "xl:text-[14px] xl:mt-[6px]"
                      )}
                      style={{
                        color: item.meta_description_color ?? "#FFFFFF",
                        fontFamily: "var(--font-google-sans)",
                      }}
                    >
                      {item.meta_description}
                    </p>
                  )}

                  {/* Mobile: image inside active card */}
                  {isActive && item.meta_image && (
                    <div className="relative w-full aspect-590/452 mt-[6px] sm:hidden">
                      <Image
                        src={item.meta_image}
                        alt={item.meta_title ?? "Program target audience"}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1079px) 100vw, 460px"
                      />
                    </div>
                  )}
                </button>
                </RevealOnScroll>
              );
            })}
          </div>

          {/* Right: Image (desktop only) */}
          {activeImage && (
            <RevealOnScroll
              scaleStart={0.5}
              x={100}
              enableAnimation={isAnimationEnabled}
            >
                <div
                  className={cn(
                    "relative overflow-hidden shrink-0",
                    "xs:hidden",
                    "sm:block sm:w-[460px] sm:h-[353px]",
                    "md:w-[500px] md:h-[384px]",
                    "lg:w-[590px] lg:h-[452px]",
                    "xl:w-[590px] xl:h-[452px]"
                  )}
                >
                  <Image
                    src={activeImage}
                    alt={
                      sortedMetadata[activeIndex]?.meta_title ??
                      "Program target audience"
                    }
                    fill
                    className="object-cover transition-opacity duration-300"
                    sizes="(max-width: 1229px) 460px, (max-width: 1429px) 500px, (max-width: 1749px) 590px, 590px"
                  />
                </div>
            </RevealOnScroll>
          )}
        </div>

        {/* CTA Button */}
        {cta_text && (
          <div
            className={cn(
              "flex justify-center",
              "xs:mt-0 xs:w-full",
              "sm:mt-[40px]",
              "md:mt-[36px]",
              "lg:mt-[40px]",
              "xl:mt-[63px]"
            )}
          >
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <button
              type="button"
              data-cta-name="apply_now"
              onClick={handleCtaClick}
              className={cn("btn-base", "xs:w-full", "sm:w-auto")}
              style={{
                backgroundColor: cta_color,
                color: cta_text_color,
              }}
            >
              {cta_text}
            </button>
            </RevealOnScroll>
          </div>
        )}
      </div>
    </section>
  );
}

export default WhoIsThisProgramFor1;
